import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Row,
  Typography,
  Pagination,
  Spin,
  Tooltip,
  Modal,
  Input,
  Col,
  Button,
  Dropdown,
  message,
} from 'antd'
import type { MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from '../Organizations.module.less'
import { WaitOverlay } from '~/Components'
import { StaffsTable } from './StaffsTable'
import { getLeftSiderCollapsed } from '~/Store/Layout/selectors'
import { CreateUpdateStaffForm } from './CreateUpdateStaffForm'
import {
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useAntdBackendTable } from '~/Hooks/useAntdBackendTable'
import { Utils } from '~/Utils'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import { OrganizationActions, OrgModelName } from '~/Store/Organization'
import { getModalVisibility } from '~/Store/Organization/selectors'
import { StaffActions, selectStaffState } from '~/Store/Staff'
import { GroupActions } from '~/Store/Groups'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { StaffInfo, StaffPayload } from '~/Services/StaffServices'
import { UploadFileModal } from '~/Components/Upload/UploadfileModal'

export const Staffs = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const leftMenuCollapsed = useAppSelector(getLeftSiderCollapsed)
  const defaultOrg = useAppSelector(getDefaultOrganization)
  const isModalVisible = useAppSelector(
    getModalVisibility(OrgModelName.createUpdateStaffModal),
  )

  const {
    staffData,
    page,
    pageSize,
    loading,
    pagination,
    keyword,
    columnFilters,
    sort,
  } = useAppSelector(selectStaffState)

  const orgRoles = useAppSelector(state => state.organization.roles)
  const [selectedStaff, setSelectedStaff] = useState<StaffInfo | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const currentFiltersRef = useRef<any>({})
  const currentSortRef = useRef<any>(undefined)

  // Build keyword query
  const buildKeywordQuery = (keyword: string) => {
    if (!keyword.trim()) return ''

    return `(contains(userName,'${keyword}') or contains(email,'${keyword}') or contains(phoneNumber,'${keyword}') or contains(firstname,'${keyword}') or contains(lastname,'${keyword}'))`
  }

  const buildFinalFilter = (keyword: string, columnFilterQuery?: string) => {
    const keywordQuery = buildKeywordQuery(keyword)

    if (keywordQuery && columnFilterQuery)
      return `${keywordQuery} and ${columnFilterQuery}`

    return keywordQuery || columnFilterQuery || ''
  }

  // Fetch data
  const fetchStaff = useCallback(
    ({
      pageParam = page,
      pageSizeParam = pageSize,
      columnFilterQuery,
      columnFiltersData,
      sort,
    }: {
      pageParam?: number
      pageSizeParam?: number
      columnFilterQuery?: string
      columnFiltersData?: any
      sort?: any
    }) => {
      if (!defaultOrg?.guid) return

      const finalFilter = buildFinalFilter(keyword, columnFilterQuery)

      dispatch(
        StaffActions.getStaffList({
          orgId: defaultOrg.guid,
          page: pageParam,
          pageSize: pageSizeParam,
          filter: {
            queryString: finalFilter,
            columnFilters: columnFiltersData || {},
          },
          sort,
          expand: 'Licenses',
        }),
      )
    },
    [defaultOrg?.guid, keyword, dispatch],
  )

  // Initial load
  useEffect(() => {
    if (defaultOrg?.guid && !hasInitialized) {
      const columnFilterQuery = Utils.buildAntdFilterQuery(columnFilters)
      fetchStaff({
        pageParam: page,
        pageSizeParam: pageSize,
        columnFilterQuery,
        columnFiltersData: columnFilters,
        sort,
      })
      setHasInitialized(true)
    }
  }, [
    defaultOrg?.guid,
    hasInitialized,
    fetchStaff,
    page,
    pageSize,
    columnFilters,
    sort,
  ])

  // Table change
  const fetchStaffData = useCallback(
    (params: any) => {
      fetchStaff({
        pageParam: params.page,
        pageSizeParam: params.pageSize,
        columnFilterQuery: params.filter,
        sort: params.sort,
      })
    },
    [fetchStaff],
  )

  const { handleTableChange: handleAntdTableChange } =
    useAntdBackendTable(fetchStaffData)

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    currentFiltersRef.current = filters
    dispatch(StaffActions.setColumnFilters(filters))
    
    if (sorter?.field && sorter?.order) {
      const sortData = { field: sorter.field, order: sorter.order }
      currentSortRef.current = sortData
      dispatch(StaffActions.setSort(sortData))
    } else if (sorter?.field === undefined) {
      currentSortRef.current = undefined
      dispatch(StaffActions.setSort(undefined))
    }

    handleAntdTableChange(pagination, filters, sorter)
  }

  // Reset filters khi đổi organization hoặc unmount
  useEffect(() => {
    return () => {
      dispatch(StaffActions.resetFilter())
    }
  }, [dispatch, defaultOrg?.guid])

  // Search
  useEffect(() => {
    if (!defaultOrg?.guid || !hasInitialized) return

    const timer = setTimeout(() => {
      const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)

      fetchStaff({
        pageParam: 1,
        pageSizeParam: pageSize,
        columnFilterQuery,
        columnFiltersData: currentFiltersRef.current,
        sort: currentSortRef.current,
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [
    keyword,
    defaultOrg?.guid,
    fetchStaff,
    pageSize,
  ])

  // Pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
    fetchStaff({
      pageParam: page,
      pageSizeParam: pageSize,
      columnFilterQuery,
      columnFiltersData: currentFiltersRef.current,
      sort: currentSortRef.current,
    })
  }

  const showCreateUpdateStaffModal = () => {
    setSelectedStaff(null)
    dispatch(StaffActions.clearCurrentRoles())
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateStaffModal,
        value: true,
      }),
    )
  }

  const handleEdit = (staff: any) => {
    setSelectedStaff(staff)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateStaffModal,
        value: true,
      }),
    )
  }

  const handleCancel = () => {
    setSelectedStaff(null)
    dispatch(StaffActions.clearCurrentRoles())
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateStaffModal,
        value: false,
      }),
    )
  }

  const handleSubmit = (values: any) => {
    if (!defaultOrg?.guid) return

    const parts = values.fullName.trim().split(/\s+/)
    const firstname = parts.pop() || ''
    const lastname = parts.join(' ')

    // Map role IDs to role names for assignRoles API
    const selectedRoleData =
      values.roles
        ?.map((roleId: string) => {
          const role = orgRoles.find(r => r.id === roleId)
          return role
            ? {
                organizationId: defaultOrg.guid,
                roleName: role.name,
              }
            : null
        })
        .filter(Boolean) || []

    const staffData: StaffPayload = {
      // userCode: values.userCode,
      userName: values.email,
      email: values.email,
      firstname: firstname || '',
      lastname: lastname || '',
      isActive: values.isActive,
      lockoutEnabled: values.lockoutEnabled,
      phoneNumber: values.phone || '',
      profile: {
        checkInDate: values.checkInDate?.toISOString(),
        note: values.note,
      },
    }
    if (values.password) {
      staffData.password = values.password
    }

    // Gán role và bộ phận
    const assignRolesAndGroup = (
      staff: any,
      pageToUse: number,
      isCreate: boolean,
    ) => {
      let rolesToAssign: { organizationId: string; roleName: string }[] =
        selectedRoleData
      let rolesToRemove: { organizationId: string; roleName: string }[] = []

      if (isCreate) {
        // Khi tạo mới: lọc bỏ role "Member" vì đã có sẵn
        rolesToAssign = selectedRoleData.filter(
          (role: any) => role.roleName.toLowerCase() !== 'member',
        )
      } else {
        const currentRoleNames = selectedStaff?.organizationRoles || []
        const selectedRoleNames = selectedRoleData.map((r: any) => r.roleName)
        // Role mới
        rolesToAssign = selectedRoleData.filter(
          (role: any) => currentRoleNames.indexOf(role.roleName) === -1,
        )
        // Role cần xóa
        rolesToRemove = currentRoleNames
          .filter((roleName: string) => !selectedRoleNames.includes(roleName))
          .map((roleName: string) => ({
            organizationId: defaultOrg.guid,
            roleName: roleName,
          }))
      }

      const hasRolesToAssign = rolesToAssign.length > 0
      const hasRolesToRemove = rolesToRemove.length > 0
      const hasDepartment = !!values.organizationJobId

      if (!hasRolesToAssign && !hasRolesToRemove && !hasDepartment) {
        handleCancel()
        const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
        fetchStaff({
          pageParam: pageToUse,
          pageSizeParam: pageSize,
          columnFilterQuery,
          columnFiltersData: currentFiltersRef.current,
          sort: currentSortRef.current,
        })
        return
      }

      // Kiểm tra gán role, bộ phận và check-in date
      let assignRolesCompleted = !hasRolesToAssign
      let removeRolesCompleted = !hasRolesToRemove
      let groupCompleted = !hasDepartment

      const checkAndRefresh = () => {
        if (assignRolesCompleted && removeRolesCompleted && groupCompleted) {
          handleCancel()
          const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
          fetchStaff({
            pageParam: pageToUse,
            pageSizeParam: pageSize,
            columnFilterQuery,
            columnFiltersData: currentFiltersRef.current,
            sort: currentSortRef.current,
          })
        }
      }

      // Assign new roles
      if (hasRolesToAssign) {
        dispatch(
          StaffActions.assignRoles({
            userId: staff.id,
            roleData: rolesToAssign as any,
            onSuccess: () => {
              assignRolesCompleted = true
              checkAndRefresh()
            },
            onFailure: error => {
              message.error(error)
              assignRolesCompleted = true
              checkAndRefresh()
            },
          }),
        )
      }

      // Remove old roles
      if (hasRolesToRemove) {
        dispatch(
          StaffActions.removeRoles({
            userId: staff.id,
            roleData: rolesToRemove as any,
            onSuccess: () => {
              removeRolesCompleted = true
              checkAndRefresh()
            },
            onFailure: error => {
              message.error(error)
              removeRolesCompleted = true
              checkAndRefresh()
            },
          }),
        )
      }

      // Assign group
      if (hasDepartment) {
        dispatch(
          GroupActions.assignGroupToStaff({
            jobId: values.organizationJobId,
            organizationId: defaultOrg.guid,
            userId: staff.id,
            onSuccess: () => {
              groupCompleted = true
              checkAndRefresh()
            },
            onFailure: error => {
              message.error(error)
              groupCompleted = true
              checkAndRefresh()
            },
          }),
        )
      }
    }

    if (selectedStaff) {
      dispatch(
        StaffActions.createStaff({
          orgId: defaultOrg.guid,
          data: staffData,
          onSuccess: staff => {
            assignRolesAndGroup(staff, page, false)
          },
        }),
      )
    } else {
      dispatch(
        StaffActions.createStaff({
          orgId: defaultOrg.guid,
          data: staffData,
          onSuccess: staff => {
            assignRolesAndGroup(staff, 1, true)
          },
        }),
      )
    }
  }

  const handleDelete = (userIds: string[]) => {
    if (!defaultOrg?.guid) return

    Modal.confirm({
      title: t('staff.confirmDelete'),
      content: t('staff.confirmDeleteMessage', { count: userIds.length }),
      okText: t('remove'),
      cancelText: t('cancel'),
      okType: 'danger',
      onOk: () => {
        dispatch(
          StaffActions.deleteStaff({
            orgId: defaultOrg.guid,
            userIds,
            onSuccess: () => {
              const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
              fetchStaff({
                pageParam: page,
                pageSizeParam: pageSize,
                columnFilterQuery,
                columnFiltersData: currentFiltersRef.current,
                sort: currentSortRef.current,
              })
              setSelectedRowKeys([])
            },
          }),
        )
      },
    })
  }

  const handleExportAll = () => {
    if (!defaultOrg?.guid) return

    dispatch(
      StaffActions.exportAllStaffs({
        orgId: defaultOrg.guid,
        onSuccess: data => {
          Utils.downloadFile(data, `staffs-all-${new Date().getTime()}.xlsx`)
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleExportCurrentPage = () => {
    if (!defaultOrg?.guid) return
    const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
    const finalFilter = buildFinalFilter(keyword, columnFilterQuery)

    dispatch(
      StaffActions.exportStaffsInCurrentPage({
        orgId: defaultOrg.guid,
        page,
        pageSize,
        filter: {
          queryString: finalFilter,
          columnFilters: currentFiltersRef.current,
        },
        sort: currentSortRef.current,
        expand: 'Licenses',
        onSuccess: data => {
          Utils.downloadFile(
            data,
            `staffs-page-${page}-${new Date().getTime()}.xlsx`,
          )
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleExportSelected = () => {
    if (!defaultOrg?.guid || selectedRowKeys.length === 0) return

    dispatch(
      StaffActions.exportSelectedStaffs({
        orgId: defaultOrg.guid,
        userIds: selectedRowKeys,
        onSuccess: data => {
          Utils.downloadFile(
            data,
            `staffs-selected-${selectedRowKeys.length}-${new Date().getTime()}.xlsx`,
          )
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true)
  }

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false)
  }

  const uploadStaffActionCreator = (file: File) => {
    if (!defaultOrg?.guid) return
    return StaffActions.importStaffs({
      orgId: defaultOrg.guid,
      file,
      onSuccess: () => {
        message.success(t('uploadSuccess'))
        const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
        fetchStaff({
          pageParam: 1,
          pageSizeParam: pageSize,
          columnFilterQuery,
          columnFiltersData: currentFiltersRef.current,
          sort: currentSortRef.current,
        })
      },
      onFailure: () => {
        message.error(t('uploadFail'))
      },
    })
  }

  const downloadMenuItems: MenuProps['items'] = [
    {
      key: 'all',
      label: t('exportAll'),
      onClick: handleExportAll,
    },
    {
      key: 'current',
      label: t('exportCurrentPage'),
      onClick: handleExportCurrentPage,
    },
    {
      key: 'selected',
      label: t('exportSelected'),
      onClick: handleExportSelected,
      disabled: selectedRowKeys.length === 0,
    },
  ]

  return (
    <WaitOverlay name={''} size="large" wrapperClassName={styles.FullOverlay}>
      <Modal
        className={styles.CustomModal}
        title={selectedStaff ? t('staff.editStaff') : t('staff.createStaff')}
        visible={isModalVisible}
        onCancel={handleCancel}
        width={700}
        footer={null}
        destroyOnClose
      >
        <CreateUpdateStaffForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialData={selectedStaff}
          isLoading={loading}
        />
      </Modal>

      <UploadFileModal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        actionCreator={uploadStaffActionCreator}
      />

      <div className={styles.OrganizationBox}>
        <Row align="stretch" style={{ marginBottom: '30px' }}>
          <Typography.Title style={{ flex: 1 }} level={4}>
            {t('staff.staffManagement')}
          </Typography.Title>

          <Row gutter={8}>
            <Col flex="auto">
              <Input
                style={{ height: '32px' }}
                prefix={<SearchOutlined />}
                placeholder={t('search')}
                value={keyword}
                onChange={e =>
                  dispatch(StaffActions.setKeyword(e.target.value))
                }
                allowClear
              />
            </Col>
            <Col>
              <Tooltip title={t('staff.createStaff')}>
                <Button
                  icon={<PlusOutlined style={{ fontSize: '12px' }} />}
                  onClick={showCreateUpdateStaffModal}
                />
              </Tooltip>
            </Col>
            <Col>
              <Dropdown menu={{ items: downloadMenuItems }} trigger={['hover']}>
                <Button icon={<DownloadOutlined />}>{t('download')}</Button>
              </Dropdown>
            </Col>
            <Col>
              <Button icon={<UploadOutlined />} onClick={handleOpenUploadModal}>
                {t('upload')}
              </Button>
            </Col>
          </Row>
        </Row>

        <Spin spinning={loading} size="large">
          <div className={styles.ListContent}>
            <StaffsTable
              data={staffData}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTableChange={handleTableChange}
              columnFilters={columnFilters}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={setSelectedRowKeys}
            />
          </div>
        </Spin>

        <div
          className={styles.Pagination}
          style={{
            width: `calc(100% - ${leftMenuCollapsed ? '146' : '266'}px)`,
          }}
        >
          <Pagination
            current={pagination.current}
            total={pagination.total}
            pageSize={pagination.pageSize}
            showSizeChanger
            responsive
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      </div>
    </WaitOverlay>
  )
}
